/**
 * Securities search for the command palette.
 *
 * Backed by the Tantivy instruments-master index via
 * `GET /api/v1/search/instruments`. Only fires once the user has typed at
 * least two characters, so an empty palette never touches the network.
 */

import { useQuery } from "@tanstack/react-query";
import { devToken } from "@itoresearch/api-client";

import { api, ApiError } from "../../lib/api-client";

export function useSecuritiesSearch(raw: string) {
  const q = raw.trim();
  return useQuery({
    queryKey: ["search", "instruments", q],
    enabled: q.length >= 2,
    staleTime: 30_000,
    // API errors are definitive answers — 401 (not signed in) and 503 (index
    // not ready) won't fix themselves on a retry. Only transient network
    // failures (`fetch` rejections, not ApiError) are retried.
    retry: (failureCount, error) =>
      error instanceof ApiError ? false : failureCount < 2,
    queryFn: ({ signal }) =>
      api.search.instruments(
        { q, limit: 8 },
        // Local dev / e2e: the API accepts a `Bearer dev.<user>.<org>` token
        // instead of a session cookie, which the web app doesn't have in dev.
        import.meta.env.DEV ? devToken() : undefined,
        signal,
      ),
  });
}
